/**
 * `order_items.item_category` — the client half of "orders are all-hire or
 * all-non-hire" (owner ruling, 2026-09-02). Design note:
 * `docs/superpowers/specs/2026-09-02-orders-all-hire-or-none-design.md`.
 *
 * A CONVENIENCE, NEVER A GATE — `server/helpers/orderHireMode.js` is the
 * real enforcement, on every writer. This file exists so the form can grey
 * the right options and block "Add Item" before a doomed request is even
 * sent; a caller that bypasses the UI is refused independently on the
 * server, unconditionally.
 *
 * 🔧 CORRECTED, FIX ROUND 2 (adversarial re-review): this used to say
 * "deliberately simpler than the server's two-phase algorithm" — the server
 * no longer HAS a two-phase algorithm (that was the retired item_category
 * design; the owner's finding-3 ruling moved the server gate to `is_hire`,
 * which needs no holder/phase concept at all — see
 * `server/helpers/orderHireMode.js`'s own docblock). What is still true,
 * restated without the stale comparison: this file is pure LIVE EDITING
 * STATE, not a "stored vs resubmitted" comparison, so there is no
 * echo-hatch here — that exists on the SERVER to make decision 2
 * ("not retroactive") correct against a database, which is not a question
 * a client-only form has to answer. The client only ever asks one question
 * of its own in-progress `items` array: "what mode, if any, has been
 * established so far, and by which line?" — still keyed on
 * `item_category` (never `is_hire` directly), because that is the field
 * the buyer's own dropdown writes and the client never produces a
 * mismatched submission for the server to disagree with.
 */

/**
 * @param {string|null|undefined} category
 * @returns {'hire'|'non_hire'|null}
 */
export function itemHireMode(category) {
  if (!category) return null;
  return category === 'Hire' ? 'hire' : 'non_hire';
}

/**
 * The mode established by the first item (lowest index) with a non-blank
 * category, or `null` if nothing has been decided yet.
 */
export function resolveOrderMode(items) {
  for (const item of items || []) {
    const mode = itemHireMode(item.item_category);
    if (mode) return mode;
  }
  return null;
}

/**
 * The index of the item whose category ESTABLISHES the order's mode — the
 * one line whose own dropdown must stay fully open, since every other
 * line's options are derived from it. `null` when no mode is established
 * yet (every item's category is blank).
 */
export function decidingItemIndex(items) {
  for (let i = 0; i < (items || []).length; i += 1) {
    if (itemHireMode(items[i].item_category)) return i;
  }
  return null;
}

/**
 * 🔻 LATEST CHANGE WINS — owner ruling, 2026-09-03, verbatim: *"If there's
 * ever a confliction the latest change wins and the other go back to
 * '--Select--'"*.
 *
 * THE BUG THIS CLOSES, reported from a real create flow: item 1 set to
 * `Hire` correctly locked item 2 to hire-only — but changing item 1 to
 * something else left item 2 sitting on `Hire`. `resolveOrderMode` reads
 * the FIRST non-blank category, so the order's mode silently flipped while
 * a stale conflicting line kept its value. Two consequences, and the second
 * is the one that matters: the form displayed a value its own dropdown now
 * greys out, and the SUBMISSION WAS MIXED — refused by the server gate,
 * which reads `is_hire`. The user's only clue would have been a rejected
 * save naming a line they had not touched.
 *
 * 🚨 IT CLEARS `is_hire` AS WELL AS THE CATEGORY, AND THAT IS LOAD-BEARING,
 * NOT TIDINESS. The server gate is keyed on `is_hire` (owner ruling
 * 2026-09-02 — see `server/helpers/orderHireMode.js`), so blanking the
 * category alone would leave a line reading "--Select--" on screen while
 * still submitting as a hire: the exact category-vs-`is_hire` divergence
 * the server rewrite exists to make impossible, reintroduced from the
 * client. `OrderForm#updateItem` is the ONLY place that couples the two
 * fields; this function is the only place that decouples them, and it must
 * clear both or neither.
 *
 * ⚠️ Hire dates and rates on a cleared line are deliberately PRESERVED —
 * only the category resets. Re-picking `Hire` restores the work, and the
 * ruling says the line goes back to "--Select--", not that its contents are
 * destroyed.
 *
 * Returns the ORIGINAL array when nothing conflicts, so an ordinary edit
 * cannot trigger a re-render through a new identity.
 *
 * @param {Array} items
 * @param {number} changedIndex the line the user just edited — never reset
 */
export function clearConflictingCategories(items, changedIndex) {
  const list = items || [];
  const changed = list[changedIndex];
  const changedMode = itemHireMode(changed && changed.item_category);
  // The edit BLANKED a category, so it establishes nothing and can conflict
  // with nothing. Never cascade a reset off a reset.
  if (!changedMode) return list;

  let mutated = false;
  const next = list.map((item, i) => {
    // 🟢 HONEST MUTATION NOTE: this guard is NOT load-bearing today and the
    // suite says so. Deleting it leaves all 21 cases green, because the
    // changed line's own mode is `changedMode` by construction, so the
    // `mode === changedMode` test on the next line already returns it
    // untouched. It is kept as a statement of the RULE ("the latest change
    // wins — never reset the line the user just edited") and as cover for a
    // future refactor that computes `changedMode` from something other than
    // this same item. Do not cite it as proven behaviour.
    if (i === changedIndex) return item;
    const mode = itemHireMode(item.item_category);
    if (!mode || mode === changedMode) return item;
    mutated = true;
    return { ...item, item_category: '', is_hire: false };
  });
  return mutated ? next : list;
}
